// De gemeten feedposten (B6 golf 1, feedbackronde 27-08): kaarten die de
// code over bestaande data berekent — $0 per ronde, geen model. Vier vormen:
//
//   versnellingssignaal  het publicatietempo van een trend verdubbelt
//   cijferkaart          regionale afwijking + het cijfer van de maand
//   vooruitblik          vastliggende toekomst binnen 90 dagen
//   opkomende termen     vernieuwingsankers die net opdoken
//
// HERGOTEN IN DE POETSRONDE 28-08. Golf 1 stond als losse widget-strook
// áchter de stroom (grijs voorraad-label, 14px-titel, geen hover, geen
// modal, buiten de sorteermachine) terwijl de scenariokaart uit dezelfde
// ronde wél het kaart-idioom kreeg — het gemeten "aangeplakt"-gevoel van de
// owner. Elke post is nu een volwaardige kaart: sprekerspil (synthese, want
// wij spreken) + groene "✓ gemeten"-badge, de 19px-kop, hover, en een klik
// die eerst de onderbouwing opent (GemetenPostModal) in plaats van weg te
// springen — dezelfde klikgrammatica als elke andere kaart (owner 26-08).
// De posten sorteren mee in de stroom via `weefPosten`.
//
// De belofte blijft het woord "gemeten": elk getal is na te rekenen, en een
// kaart zonder data bestaat niet.

import { useState } from "react";
import type { ReactNode } from "react";

import type { PostenResponse, Cijferpost, KaartLanding } from "../contracts";
import { PILLEN } from "../lib/kaartas";
import { color, font, radius, size, weight } from "../tokens";
import { Modal, Sparklijn } from "./ui";

export type PostOpenen = (landing: KaartLanding) => void;

type Versneller = PostenResponse["versnellers"][number];
type VooruitblikRij = PostenResponse["vooruitblik"][number];
type Term = PostenResponse["opkomendeTermen"][number];

/** Eén gemeten post zoals hij in de stroom staat. */
export type GemetenPost =
  | { soort: "versneller"; sleutel: string; versneller: Versneller }
  | { soort: "cijfer"; sleutel: string; label: string; cijfer: Cijferpost }
  | { soort: "vooruitblik"; sleutel: string; rijen: readonly VooruitblikRij[] }
  | { soort: "termen"; sleutel: string; termen: readonly Term[] };

const nf = new Intl.NumberFormat("nl-NL");

/** De posten in stroomvolgorde. Een lege laag levert geen post: geen data
 *  betekent geen kaart. */
export function gemetenPosten(posten: PostenResponse | null): GemetenPost[] {
  if (!posten) return [];
  const uit: GemetenPost[] = [];
  for (const v of posten.versnellers) {
    uit.push({ soort: "versneller", sleutel: `versneller-${v.trendId}`, versneller: v });
  }
  if (posten.regionaleAfwijking) {
    uit.push({
      soort: "cijfer",
      sleutel: "regionale-afwijking",
      label: "Onze regio wijkt af",
      cijfer: posten.regionaleAfwijking,
    });
  }
  if (posten.cijferVanDeMaand) {
    uit.push({
      soort: "cijfer",
      sleutel: "cijfer-van-de-maand",
      label: "Het cijfer van de maand",
      cijfer: posten.cijferVanDeMaand,
    });
  }
  if (posten.vooruitblik.length > 0) {
    uit.push({ soort: "vooruitblik", sleutel: "vooruitblik", rijen: posten.vooruitblik });
  }
  if (posten.opkomendeTermen.length > 0) {
    uit.push({ soort: "termen", sleutel: "termen", termen: posten.opkomendeTermen });
  }
  return uit;
}

/** Hoeveel kaarten de posten in de stroom zetten — voor de pil-telling
 *  (W1c §7: het getal op de pil moet na te tellen zijn in de lijst). De
 *  vooruitblik en de termenwolk tellen als één kaart, precies zoals ze er
 *  als één kaart staan. */
export function postenTelling(posten: PostenResponse | null): number {
  return gemetenPosten(posten).length;
}

/**
 * De posten door de gesorteerde kaarten weven, gelijkmatig verdeeld, in
 * plaats van als blok achteraan. De sortering zelf blijft van de kaarten
 * (sorteerscore, spreidingsregel); de posten hebben geen redactionele rang
 * en claimen er dus ook geen — gelijkmatig verdelen is de eerlijke plek.
 */
export type StroomItem<T> =
  | { soort: "kaart"; kaart: T }
  | { soort: "post"; post: GemetenPost };

export function weefPosten<T>(kaarten: readonly T[], posten: readonly GemetenPost[]): StroomItem<T>[] {
  const uit: StroomItem<T>[] = [];
  if (posten.length === 0) return kaarten.map((kaart) => ({ soort: "kaart", kaart }));
  const stap = Math.max(1, Math.round(kaarten.length / (posten.length + 1)));
  let p = 0;
  kaarten.forEach((kaart, i) => {
    uit.push({ soort: "kaart", kaart });
    if ((i + 1) % stap === 0 && p < posten.length) {
      uit.push({ soort: "post", post: posten[p]! });
      p += 1;
    }
  });
  while (p < posten.length) {
    uit.push({ soort: "post", post: posten[p]! });
    p += 1;
  }
  return uit;
}

/* ── de dichte kaart, in het EditieKaart-idioom ─────────────────────────── */

const pil = PILLEN.synthese;

/** De kopregel: sprekerspil + de groene gemeten-badge. Zelfde maatvoering
 *  als PilRij/Badge in EditieKaart — de posten zijn kaarten, geen widgets. */
function PostPilRij() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 10,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10.5,
          fontWeight: weight.medium,
          color: pil.kleur,
          background: `${pil.kleur}12`,
          border: `1px solid ${pil.kleur}33`,
          borderRadius: radius.pill,
          padding: "2.5px 10px",
          whiteSpace: "nowrap",
        }}
        title={`${pil.label}: ${pil.spreker}`}
      >
        <span
          style={{ width: 5, height: 5, borderRadius: 99, background: pil.kleur, display: "inline-block" }}
        />
        {pil.label}
      </span>
      <span
        title="Door de code berekend over bestaande data — geen model, elk getal is na te rekenen."
        style={{
          fontSize: size.label,
          color: color.accentGreen,
          background: `${color.accentGreen}12`,
          border: `1px solid ${color.accentGreen}33`,
          borderRadius: radius.pill,
          padding: "3px 9px",
          whiteSpace: "nowrap",
        }}
      >
        ✓ gemeten
      </span>
    </div>
  );
}

const kopStijl = {
  fontSize: 19,
  fontWeight: weight.bold,
  color: color.ink,
  lineHeight: 1.3,
  letterSpacing: "-0.012em",
} as const;

/** De herkomstregel onderaan — het Bewijsregel-idioom van EditieKaart. */
function PostVoet({ sterk, rest }: { sterk: string; rest: string }) {
  return (
    <div
      style={{
        fontSize: size.label,
        color: color.faint,
        paddingTop: 10,
        marginTop: 12,
        borderTop: `1px solid ${color.line}`,
      }}
    >
      <b style={{ color: color.soft, fontWeight: weight.medium }}>{sterk}</b>
      {` · ${rest}`}
    </div>
  );
}

/** De kop van een post — ook de modaltitel, zodat kaart en modal één naam
 *  dragen. */
export function postKop(post: GemetenPost): string {
  if (post.soort === "versneller") return post.versneller.titel;
  if (post.soort === "cijfer") return `${cijferTekst(post.cijfer)} ${post.cijfer.sleutel || post.cijfer.indicator}`;
  if (post.soort === "vooruitblik") return "Wat er al vastligt — de komende 90 dagen";
  return "Opkomende termen in het corpus";
}

function cijferTekst(c: Cijferpost): string {
  const eenheid = c.veranderingSoort === "procentpunt" ? " procentpunt" : "%";
  return `${c.veranderingRegio > 0 ? "+" : ""}${nf.format(c.veranderingRegio)}${eenheid}`;
}

function landelijkTekst(c: Cijferpost): string | null {
  if (c.veranderingLandelijk === undefined) return null;
  const eenheid = c.veranderingSoort === "procentpunt" ? " procentpunt" : "%";
  return `${c.veranderingLandelijk > 0 ? "+" : ""}${nf.format(c.veranderingLandelijk)}${eenheid}`;
}

function VooruitblikRijen({
  rijen,
  onOpen,
  klikbaar = false,
}: {
  rijen: readonly VooruitblikRij[];
  onOpen?: PostOpenen;
  klikbaar?: boolean;
}) {
  return (
    <div>
      {rijen.map((p, i) => {
        // Alleen een rij die ergens heen kán is een knop — de focusbare dode
        // rijen en het kapotte toetsenbordpad uit golf 1 bestaan niet meer.
        const landing: KaartLanding | null = !klikbaar
          ? null
          : p.soort === "subsidie"
            ? { soort: "tab", tab: "funding" }
            : p.documentId
              ? { soort: "paneel", tab: "sources", id: p.documentId }
              : null;
        const open = landing && onOpen ? () => onOpen(landing) : undefined;
        return (
          <div
            key={i}
            onClick={open}
            role={open ? "button" : undefined}
            tabIndex={open ? 0 : undefined}
            onKeyDown={(e) => {
              if (open && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                open();
              }
            }}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "baseline",
              padding: "5px 0",
              borderTop: i > 0 ? `1px solid ${color.lineFaint}` : undefined,
              cursor: open ? "pointer" : "default",
            }}
          >
            <span
              style={{
                fontSize: size.label,
                color: color.faint,
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}
            >
              {p.datum}
            </span>
            <span
              style={{
                fontSize: size.label,
                color: p.soort === "subsidie" ? color.accentAmber : color.soft,
                flexShrink: 0,
              }}
            >
              {p.soort}
            </span>
            <span style={{ fontSize: size.meta, color: color.ink, minWidth: 0 }}>{p.titel}</span>
          </div>
        );
      })}
    </div>
  );
}

function TermenWolk({ termen }: { termen: readonly Term[] }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {termen.map((t) => (
        <span
          key={t.term}
          title={`Eerste verschijning in het kwartaal vanaf ${t.eersteKwartaal.slice(0, 7)}`}
          style={{
            border: `1px solid ${color.border}`,
            borderRadius: radius.pill,
            padding: "3px 9px",
            fontSize: size.label,
            color: color.body,
          }}
        >
          {t.term} · {t.documenten}
        </span>
      ))}
    </div>
  );
}

/** De dichte kaart. Klikken opent de modal met de onderbouwing — er staat
 *  geen "open"-knop op, net als op elke andere kaart in de stroom. */
export function GemetenPostKaart({
  post,
  onDetail,
}: {
  post: GemetenPost;
  onDetail: (post: GemetenPost) => void;
}) {
  const [hover, setHover] = useState(false);
  const inhoud: ReactNode =
    post.soort === "versneller" ? (
      <>
        <div style={kopStijl}>{post.versneller.titel}</div>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 8,
          }}
        >
          <div style={{ fontSize: size.body, color: color.muted, lineHeight: 1.6 }}>
            {post.versneller.aantalVorig} → {post.versneller.aantalNu} bronnen in het kwartaal
            vanaf {post.versneller.kwartaalNu.slice(0, 7)}
          </div>
          <Sparklijn waarden={post.versneller.kwartalen.map((k) => k.aantal)} w={76} h={22} />
        </div>
      </>
    ) : post.soort === "cijfer" ? (
      <>
        <div style={kopStijl}>
          {cijferTekst(post.cijfer)} · {post.cijfer.sleutel || post.cijfer.indicator}
        </div>
        <div style={{ fontSize: size.body, color: color.muted, lineHeight: 1.6, marginTop: 8 }}>
          {post.cijfer.regio}, jaar-op-jaar
          {landelijkTekst(post.cijfer) ? ` — landelijk ${landelijkTekst(post.cijfer)}` : ""}.
        </div>
      </>
    ) : post.soort === "vooruitblik" ? (
      <>
        <div style={kopStijl}>{postKop(post)}</div>
        <div style={{ marginTop: 8 }}>
          <VooruitblikRijen rijen={post.rijen} />
        </div>
      </>
    ) : (
      <>
        <div style={kopStijl}>{postKop(post)}</div>
        <div style={{ marginTop: 10 }}>
          <TermenWolk termen={post.termen} />
        </div>
      </>
    );

  const voet =
    post.soort === "versneller"
      ? { sterk: "publicatietempo", rest: "geteld op de publicatiedatum van het bewijs" }
      : post.soort === "cijfer"
        ? {
            sterk: post.label.toLowerCase(),
            rest: `bron ${post.cijfer.bron} · t/m ${post.cijfer.periode.slice(0, 7)}`,
          }
        : post.soort === "vooruitblik"
          ? { sterk: "vastliggende datums", rest: "geen voorspelling" }
          : { sterk: "eerste verschijning", rest: "in het jongste kwartaal van het corpus" };

  return (
    <div
      onClick={() => onDetail(post)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onDetail(post);
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        border: `1px solid ${hover ? color.disabled : color.line}`,
        borderRadius: radius.card,
        background: color.surface,
        padding: "18px 20px",
        marginBottom: 8,
        fontFamily: font,
        cursor: "pointer",
        transition: "all 0.15s",
        boxShadow: hover ? "0 2px 8px rgba(0,0,0,.06)" : "none",
      }}
    >
      <PostPilRij />
      {inhoud}
      <PostVoet sterk={voet.sterk} rest={voet.rest} />
    </div>
  );
}

/* ── de modal: waar dit op rust ─────────────────────────────────────────── */

function MLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: size.label,
        fontWeight: weight.medium,
        color: color.faint,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function ModalKnop({ tekst, onClick }: { tekst: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: `1px solid ${color.border}`,
        background: color.surface,
        borderRadius: radius.input,
        padding: "8px 14px",
        fontSize: size.meta,
        fontWeight: weight.medium,
        color: color.ink,
        fontFamily: "inherit",
        cursor: "pointer",
        marginTop: 16,
      }}
    >
      {tekst}
    </button>
  );
}

/** "Waar rust dit op": de meting uitgelegd, mét de doorklik die de dichte
 *  kaart in golf 1 nog rauw als kaartklik had. */
export function GemetenPostModal({
  post,
  onSluit,
  onOpen,
}: {
  post: GemetenPost | null;
  onSluit: () => void;
  onOpen?: PostOpenen;
}) {
  if (!post) return null;
  const door = (landing: KaartLanding) => {
    onSluit();
    onOpen?.(landing);
  };
  return (
    <Modal open title={postKop(post)} subtitle={`${pil.label} · gemeten — ${pil.spreker}`} onClose={onSluit}>
      <div style={{ fontSize: size.body, color: color.muted, lineHeight: 1.65 }}>
        <MLabel>Waar dit op rust</MLabel>
        {post.soort === "versneller" && (
          <>
            <p style={{ margin: 0 }}>
              De bewijsdocumenten onder deze trend, geteld op hun publicatiedatum: {" "}
              {post.versneller.aantalVorig} in het kwartaal ervóór, {post.versneller.aantalNu}{" "}
              in het kwartaal vanaf {post.versneller.kwartaalNu.slice(0, 7)}. De code telt;
              er komt geen model aan te pas.
            </p>
            <Sparklijn
              waarden={post.versneller.kwartalen.map((k) => k.aantal)}
              w={320}
              h={44}
              style={{ marginTop: 14 }}
            />
            <ModalKnop
              tekst="Open de trend →"
              onClick={() => door({ soort: "paneel", tab: "trends", id: post.versneller.trendId })}
            />
          </>
        )}
        {post.soort === "cijfer" && (
          <>
            <p style={{ margin: 0 }}>
              Uit de cijferreeks {post.cijfer.indicator} ({post.cijfer.bron}):{" "}
              {post.cijfer.sleutel || post.cijfer.indicator} in {post.cijfer.regio} veranderde{" "}
              {cijferTekst(post.cijfer)} jaar-op-jaar
              {landelijkTekst(post.cijfer)
                ? `, waar het landelijke cijfer ${landelijkTekst(post.cijfer)} deed`
                : ""}
              . Gemeten t/m {post.cijfer.periode.slice(0, 7)}.
            </p>
            <ModalKnop
              tekst="Bekijk de reeksen op de Arbeidsmarkt-tab →"
              onClick={() => door({ soort: "tab", tab: "arbeidsmarkt" })}
            />
          </>
        )}
        {post.soort === "vooruitblik" && (
          <>
            <p style={{ margin: "0 0 10px" }}>
              Datums die al vastliggen: subsidievensters en aangekondigde regelgeving binnen 90
              dagen. Geen voorspelling — een venster met een sluitdatum en een wet met een
              ingangsdatum liggen vast.
            </p>
            <VooruitblikRijen rijen={post.rijen} onOpen={door} klikbaar />
          </>
        )}
        {post.soort === "termen" && (
          <>
            <p style={{ margin: "0 0 10px" }}>
              Vernieuwingsankers die in het jongste kwartaal voor het eerst in het corpus
              opdoken, in minstens twee documenten. Het getal is het aantal documenten.
            </p>
            <TermenWolk termen={post.termen} />
          </>
        )}
      </div>
    </Modal>
  );
}
